/**
 * TrialOfferSheet — welcome modal offering the 3-day free trial.
 *
 * Opened automatically on app entry when `subscription.trial_available` is true.
 *
 * Flow:
 *   1. If the user is not yet `news_verified`, show a "Subscribe to channel"
 *      step with a CTA that opens the news channel (Telegram deep link) and
 *      a "Я подписался" button that calls /payments/trial/check-subscription.
 *      We refuse to activate until the backend confirms membership via the
 *      Bot API (server-side getChatMember).
 *   2. Once verified, swap the primary CTA to "Активировать триал" which
 *      calls /payments/trial/activate.
 *
 * The trial is a one-time fuse on the server (User.trial_granted); once
 * activated, `trial_available` flips to false and this sheet stops auto-
 * opening on subsequent app entries.
 */

import { motion } from "framer-motion";
import {
  CheckCircle2,
  Gift,
  MessageSquare,
  Sparkles,
  Users,
  Zap,
} from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";

import { Button, Sheet } from "@/design";
import {
  useActivateTrial,
  useCheckNewsSubscription,
  useSubscription,
} from "@/api/billing";
import { cn } from "@/lib/cn";
import { haptic, tg } from "@/lib/telegram";

interface Props {
  open: boolean;
  onClose: () => void;
}

export function TrialOfferSheet({ open, onClose }: Props) {
  const { data: subscription } = useSubscription();
  const activate = useActivateTrial();
  const check = useCheckNewsSubscription();
  const [activated, setActivated] = useState(false);
  // Local flag — once the user has tapped "Перейти в канал" we know they at
  // least saw the channel, so the "Я подписался" button becomes the natural
  // next step. Purely cosmetic; the backend is the source of truth.
  const [opened, setOpened] = useState(false);

  const verified = subscription?.news_verified ?? false;
  const channelUrl = subscription?.news_channel_url ?? null;
  const needsSubscribe = !verified && !!channelUrl;

  const handleOpenChannel = () => {
    if (!channelUrl) return;
    haptic.impact("medium");
    setOpened(true);
    if (tg) {
      // Prefer the in-Telegram link when available (no browser hop).
      tg.openTelegramLink(channelUrl);
    } else {
      window.open(channelUrl, "_blank", "noopener");
    }
  };

  const handleCheck = async () => {
    try {
      const res = await check.mutateAsync();
      if (res.verified || res.gate_disabled) {
        haptic.notify("success");
        toast.success("Подписка подтверждена");
      } else {
        haptic.notify("warning");
        toast.error("Похоже, вы ещё не подписались. Попробуйте снова");
      }
    } catch (e) {
      const msg =
        (e as { message?: string })?.message ??
        "Не удалось проверить подписку";
      toast.error(msg);
      haptic.notify("error");
    }
  };

  const handleActivate = async () => {
    try {
      await activate.mutateAsync();
      haptic.notify("success");
      setActivated(true);
      toast.success("Тестовая подписка активирована");
      setTimeout(() => {
        onClose();
        setActivated(false);
      }, 1600);
    } catch (e) {
      const msg =
        (e as { message?: string })?.message ??
        "Не удалось активировать. Попробуйте позже.";
      toast.error(msg);
      haptic.notify("error");
    }
  };

  return (
    <Sheet
      open={open}
      onClose={onClose}
      title="5 дней бесплатно"
      description={
        needsSubscribe
          ? "Подпишитесь на канал, чтобы получить триал"
          : "Попробуйте SilentPoster без оплаты"
      }
    >
      <div className="space-y-5">
        {/* Hero card */}
        <motion.div
          initial={{ scale: 0.96, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
          className={cn(
            "relative overflow-hidden rounded-3xl border border-white/[0.06]",
            "bg-gradient-to-br from-[#1F1714] via-[#15131A] to-[#0F0F12]",
            "p-6",
          )}
        >
          <div
            className="absolute -top-24 -right-20 size-72 rounded-full bg-accent/25 blur-3xl"
            aria-hidden
          />
          <div
            className="absolute -bottom-28 -left-16 size-64 rounded-full bg-accent/15 blur-3xl"
            aria-hidden
          />
          <div className="relative space-y-5">
            <div className="flex items-center gap-3">
              <div className="size-12 rounded-2xl bg-gradient-accent grid place-items-center shadow-[0_8px_32px_-8px_rgba(255,69,0,0.7)]">
                <Gift className="size-6 text-white" />
              </div>
              <div>
                <div className="text-[10px] text-text-dim uppercase tracking-wider font-medium">
                  Триал
                </div>
                <div className="text-base font-semibold">
                  Триал на 5 дней
                </div>
              </div>
            </div>

            <div className="flex items-baseline gap-2">
              <span className="text-[52px] leading-none font-semibold tabular-nums tracking-tight">
                $0
              </span>
              <span className="text-text-muted text-sm">— без карты</span>
            </div>

            <div className="grid grid-cols-2 gap-2">
              <PerkPill icon={<Users />} label="1 аккаунт" />
              <PerkPill icon={<MessageSquare />} label="До 20 чатов" />
              <PerkPill icon={<Sparkles />} label="Все шаблоны" />
              <PerkPill icon={<Zap />} label="Эмодзи Premium" />
            </div>
          </div>
        </motion.div>

        {/* Steps indicator — only when the gate is active */}
        {needsSubscribe || (verified && !!channelUrl) ? (
          <StepRow verified={verified} />
        ) : null}

        {/* Footnote */}
        <div className="text-[12px] text-text-muted leading-relaxed px-1 space-y-1.5">
          <div>
            {needsSubscribe
              ? "После подписки на канал нажмите «Я подписался» — мы автоматически проверим членство и откроем активацию."
              : "После окончания триала рассылка остановится — данные и шаблоны сохраняются. Активируйте подписку, чтобы продолжить."}
          </div>
          <div className="text-[11px] text-text-faint">
            Срок жизни аккаунта зависит от его истории и&nbsp;политики Telegram —
            сервис не несёт ответственности за блокировки.
          </div>
        </div>

        {/* Actions */}
        <div className="space-y-2">
          {needsSubscribe ? (
            <>
              <Button
                fullWidth
                size="lg"
                leftIcon={<MessageSquare />}
                onClick={handleOpenChannel}
                variant={opened ? "secondary" : "primary"}
              >
                {opened ? "Открыть канал ещё раз" : "Перейти в канал"}
              </Button>
              <Button
                fullWidth
                size="lg"
                variant={opened ? "primary" : "secondary"}
                leftIcon={<CheckCircle2 />}
                onClick={handleCheck}
                loading={check.isPending}
              >
                Я подписался
              </Button>
            </>
          ) : (
            <Button
              fullWidth
              size="lg"
              leftIcon={activated ? undefined : <Sparkles />}
              onClick={handleActivate}
              loading={activate.isPending}
              disabled={activated}
            >
              {activated ? "Активировано ✨" : "Активировать триал"}
            </Button>
          )}
          <Button variant="ghost" fullWidth onClick={onClose}>
            Позже
          </Button>
        </div>
      </div>
    </Sheet>
  );
}

function StepRow({ verified }: { verified: boolean }) {
  return (
    <div className="flex items-center gap-3 px-1">
      <Step n={1} label="Подписка" active={!verified} done={verified} />
      <div
        className={cn(
          "flex-1 h-px",
          verified ? "bg-accent/40" : "bg-white/[0.08]",
        )}
      />
      <Step n={2} label="Активация" active={verified} done={false} />
    </div>
  );
}

function Step({
  n,
  label,
  active,
  done,
}: {
  n: number;
  label: string;
  active: boolean;
  done: boolean;
}) {
  return (
    <div className="flex items-center gap-2">
      <div
        className={cn(
          "size-7 rounded-full grid place-items-center text-[12px] font-semibold transition-colors",
          done && "bg-accent/20 text-accent border border-accent/40",
          !done && active && "bg-gradient-accent text-white",
          !done && !active && "bg-white/[0.05] text-text-dim",
        )}
      >
        {done ? <CheckCircle2 className="size-4" /> : n}
      </div>
      <span
        className={cn(
          "text-[12px] font-medium",
          done && "text-accent",
          !done && active && "text-white",
          !done && !active && "text-text-dim",
        )}
      >
        {label}
      </span>
    </div>
  );
}

function PerkPill({ icon, label }: { icon: React.ReactNode; label: string }) {
  return (
    <div
      className={cn(
        "flex items-center gap-2 px-3 py-2.5 rounded-2xl",
        "bg-white/[0.04] border border-white/[0.06]",
      )}
    >
      <span className="text-accent [&_svg]:size-4">{icon}</span>
      <span className="text-[13px] font-medium text-white/90">{label}</span>
    </div>
  );
}
